import { escapeHtml, onReady } from '../../utils/dom';
import { buildMacroCategories, fetchCatalogProducts } from './catalogMacroService';
import type { CatalogProduct, MacroCategory } from './types';

const SECTION_ID = 'im-catalog-macro';
const CARD_CLASS = 'im-macro-card';
const ACTIVE_CARD_CLASS = 'is-expanded';
const PLACEHOLDER_IMAGE = '/images/placeholder.jpg';
const FULL_VIEW_PARAM = 'view';
const FULL_VIEW_VALUE = 'full';

const state = {
  products: [] as CatalogProduct[],
  macros: [] as MacroCategory[],
  loading: false,
  loaded: false,
  error: '',
  activeMacroId: '',
  fullCatalogOpen: false
};

function normalizeText(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

function isCatalogRoute(): boolean {
  const path = window.location.pathname.toLowerCase().replace(/\/+$/, '');
  return path === '/catalog' || path.endsWith('/catalog');
}

function wantsFullCatalogFromUrl(): boolean {
  const url = new URL(window.location.href);
  return url.searchParams.get(FULL_VIEW_PARAM) === FULL_VIEW_VALUE || url.hash === '#catalogo-completo';
}

function syncFullCatalogStateFromUrl(): void {
  state.fullCatalogOpen = wantsFullCatalogFromUrl();
}

function updateUrlForFullCatalog(open: boolean): void {
  const url = new URL(window.location.href);

  if (open) {
    url.searchParams.set(FULL_VIEW_PARAM, FULL_VIEW_VALUE);
    url.hash = 'catalogo-completo';
  } else {
    url.searchParams.delete(FULL_VIEW_PARAM);
    if (url.hash === '#catalogo-completo') url.hash = '';
  }

  history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

function removeSection(): void {
  document.getElementById(SECTION_ID)?.remove();
  document.body.classList.remove('im-catalog-preview-mode');
}

function findSearchInput(): HTMLInputElement | null {
  return (
    Array.from(document.querySelectorAll<HTMLInputElement>('input')).find((input) => {
      const haystack = normalizeText([input.placeholder, input.name, input.id, input.getAttribute('aria-label')].join(' '));
      return haystack.includes('cerca prodotti') || (haystack.includes('cerca') && haystack.includes('prodotti'));
    }) || null
  );
}

function findProductCountBlock(): HTMLElement | null {
  const candidates = Array.from(document.querySelectorAll<HTMLElement>('section, div, main, p, h2, h3'));
  return (
    candidates.find((element) => {
      const text = normalizeText(element.textContent || '');
      return text.includes('mostrando') && text.includes('prodotti') && text.length < 260;
    }) || null
  );
}

function findFilterRow(): HTMLElement | null {
  const input = findSearchInput();
  if (!input) return null;

  let current: HTMLElement | null = input.parentElement;
  for (let depth = 0; current && depth < 7; depth += 1) {
    const comboboxes = current.querySelectorAll('[role="combobox"], [aria-haspopup="listbox"], select').length;
    const buttons = current.querySelectorAll('button').length;
    const hasHome = normalizeText(current.textContent || '').includes('home');

    if ((comboboxes >= 2 && buttons >= 1) || (hasHome && comboboxes >= 1)) return current;
    current = current.parentElement;
  }

  return input.closest('section, div');
}

function findCatalogResultsContainer(): HTMLElement | null {
  const counter = findProductCountBlock();
  if (!counter) return null;

  let current: HTMLElement | null = counter;
  for (let depth = 0; current && depth < 8; depth += 1) {
    const imgCount = current.querySelectorAll('img').length;
    const buttonCount = current.querySelectorAll('button').length;
    const text = normalizeText(current.textContent || '');

    if (text.includes('mostrando') && text.includes('prodotti') && (imgCount >= 4 || buttonCount >= 4)) {
      return current;
    }

    current = current.parentElement;
  }

  return counter.parentElement;
}

function findInsertionAnchor(): HTMLElement | null {
  const filterRow = findFilterRow();
  if (filterRow) return filterRow;

  const counter = findProductCountBlock();
  if (counter) return counter;

  return null;
}

function ensureSection(): HTMLElement | null {
  if (!isCatalogRoute()) {
    removeSection();
    return null;
  }

  const existing = document.getElementById(SECTION_ID);
  if (existing) return existing;

  const anchor = findInsertionAnchor();
  if (!anchor || !anchor.parentElement) return null;

  const section = document.createElement('section');
  section.id = SECTION_ID;
  section.className = 'im-catalog-macro-section';

  anchor.parentElement.insertBefore(section, anchor);

  section.addEventListener('click', handleSectionClick);
  return section;
}

function imageUrl(product: CatalogProduct): string {
  const url = String(product.image || '').trim();
  return url || PLACEHOLDER_IMAGE;
}

function renderProductPreview(product: CatalogProduct): string {
  const company = String(product.company || '').trim();
  return `
    <button type="button" class="im-macro-product" data-im-product-search="${escapeHtml(product.name)}" title="Cerca ${escapeHtml(product.name)} nel catalogo completo">
      <span class="im-macro-product-img">
        <img src="${escapeHtml(imageUrl(product))}" alt="${escapeHtml(product.name)}" loading="lazy" onerror="this.src='${PLACEHOLDER_IMAGE}'">
      </span>
      <span class="im-macro-product-meta">
        <strong>${escapeHtml(product.name)}</strong>
        ${company ? `<small>${escapeHtml(company)}</small>` : ''}
      </span>
    </button>
  `;
}

function renderRibbonSlide(product: CatalogProduct): string {
  return `
    <button type="button" class="im-macro-ribbon-item" data-im-product-search="${escapeHtml(product.name)}" title="Apri nel catalogo completo: ${escapeHtml(product.name)}">
      <img src="${escapeHtml(imageUrl(product))}" alt="${escapeHtml(product.name)}" loading="lazy" onerror="this.src='${PLACEHOLDER_IMAGE}'">
      <span>${escapeHtml(product.name)}</span>
    </button>
  `;
}

function renderMacroCard(macro: MacroCategory): string {
  const isActive = state.activeMacroId === macro.id;
  const expandedProducts = macro.products.slice(0, 6);
  const previewProducts = macro.preview.slice(0, 6);
  const ribbonProducts = [...previewProducts, ...previewProducts];
  const companies = macro.companies.length ? macro.companies : ['Prodotti a catalogo'];

  return `
    <article class="${CARD_CLASS} ${isActive ? ACTIVE_CARD_CLASS : ''}" data-im-macro-id="${escapeHtml(macro.id)}" data-im-category="${escapeHtml(macro.title)}">
      <div class="im-macro-card-top">
        <div class="im-macro-card-copy">
          <p class="im-macro-card-kicker">Macrocategoria</p>
          <h3>${escapeHtml(macro.title)}</h3>
          <div class="im-macro-stats">
            <span>${macro.count} prodotti</span>
            <span>${escapeHtml(companies.slice(0, 2).join(' · '))}</span>
          </div>
        </div>
        <button type="button" class="im-macro-open-btn" data-im-open-category="${escapeHtml(macro.title)}">Apri nel catalogo</button>
      </div>

      <div class="im-macro-ribbon-wrap" aria-label="Anteprima dinamica prodotti ${escapeHtml(macro.title)}">
        <div class="im-macro-ribbon-track">
          ${ribbonProducts.map(renderRibbonSlide).join('')}
        </div>
      </div>

      <div class="im-macro-pill-row">
        ${companies.slice(0, 4).map((company) => `<span class="im-macro-pill">${escapeHtml(company)}</span>`).join('')}
      </div>

      <div class="im-macro-preview-head">
        <div>
          <strong>Anteprima prodotti</strong>
          <small>Selezione rapida dei prodotti più rappresentativi</small>
        </div>
        <button type="button" class="im-macro-expand-btn" data-im-toggle-macro="${escapeHtml(macro.id)}">${isActive ? 'Riduci dettagli' : 'Vedi dettagli'}</button>
      </div>

      <div class="im-macro-preview ${isActive ? 'is-open' : ''}" aria-label="Anteprima prodotti ${escapeHtml(macro.title)}">
        ${expandedProducts.map(renderProductPreview).join('')}
      </div>
    </article>
  `;
}

function renderKey(): string {
  return [
    state.loading ? 'loading' : 'idle',
    state.loaded ? 'loaded' : 'new',
    state.error,
    state.activeMacroId,
    state.products.length,
    state.fullCatalogOpen ? 'full-open' : 'preview',
    state.macros.map((macro) => `${macro.id}:${macro.count}`).join('|')
  ].join('::');
}

function render(section: HTMLElement): void {
  const nextRenderKey = renderKey();
  if (section.dataset.imMacroRenderKey === nextRenderKey) return;
  section.dataset.imMacroRenderKey = nextRenderKey;

  if (state.loading && !state.loaded) {
    section.innerHTML = `
      <div class="im-macro-shell is-loading">
        <div class="im-macro-title-row">
          <div>
            <p class="im-macro-eyebrow">Catalogo guidato</p>
            <h2>Esplora per macrocategorie</h2>
            <span>Sto organizzando i prodotti in una preview guidata più intuitiva.</span>
          </div>
        </div>
        <div class="im-macro-skeleton-grid"><i></i><i></i><i></i></div>
      </div>
    `;
    return;
  }

  if (state.error) {
    section.innerHTML = `
      <div class="im-macro-shell is-error">
        <div class="im-macro-title-row">
          <div>
            <p class="im-macro-eyebrow">Catalogo guidato</p>
            <h2>Macrocard temporaneamente non disponibili</h2>
            <span>${escapeHtml(state.error)}</span>
          </div>
          <button type="button" class="im-macro-retry" data-im-retry-macros>Riprova</button>
        </div>
      </div>
    `;
    return;
  }

  if (!state.macros.length) {
    section.innerHTML = `
      <div class="im-macro-shell">
        <div class="im-macro-title-row">
          <div>
            <p class="im-macro-eyebrow">Catalogo guidato</p>
            <h2>Esplora per macrocategorie</h2>
            <span>Le macrocard appariranno appena saranno disponibili prodotti con categoria.</span>
          </div>
        </div>
      </div>
    `;
    return;
  }

  const visibleMacros = state.macros.slice(0, 12);
  const totalProducts = state.products.length;

  section.innerHTML = `
    <div class="im-macro-shell">
      <div class="im-macro-hero">
        <div>
          <p class="im-macro-eyebrow">Catalogo guidato</p>
          <h2>Esplora il catalogo per macrocategorie</h2>
          <span>${visibleMacros.length} macrocard · ${totalProducts} prodotti organizzati con anteprima dinamica</span>
        </div>
        <div class="im-macro-hero-actions">
          <button type="button" class="im-macro-reset" data-im-reset-category>Reset filtri</button>
          <button type="button" class="im-macro-full-btn" data-im-open-full-catalog>Apri catalogo completo</button>
        </div>
      </div>

      <div class="im-macro-guide-row">
        <div class="im-macro-guide-pill">1. Scegli una macrocategoria</div>
        <div class="im-macro-guide-pill">2. Guarda l'anteprima scorrevole</div>
        <div class="im-macro-guide-pill">3. Apri il catalogo completo solo quando vuoi</div>
      </div>

      <div class="im-macro-grid">
        ${visibleMacros.map(renderMacroCard).join('')}
      </div>

      <div class="im-macro-bottom-cta ${state.fullCatalogOpen ? 'is-open' : ''}">
        <div class="im-macro-bottom-copy">
          <strong>Vuoi consultare l'elenco completo dei prodotti?</strong>
          <span>Apri il catalogo completo con tutti i filtri, la ricerca e la griglia completa dei prodotti.</span>
        </div>
        <div class="im-macro-bottom-actions">
          <button type="button" class="im-macro-full-btn" data-im-open-full-catalog>Apri catalogo completo</button>
          ${state.fullCatalogOpen ? '<button type="button" class="im-macro-collapse-btn" data-im-close-full-catalog>Torna alla preview guidata</button>' : ''}
        </div>
      </div>
    </div>
  `;
}

function updateSection(): void {
  const section = ensureSection();
  if (!section) return;
  render(section);
}

function setNativeInputValue(input: HTMLInputElement, value: string): void {
  const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
  if (setter) setter.call(input, value);
  else input.value = value;

  input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: value }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

function scrollToCatalogList(): void {
  const target = findFilterRow() || findProductCountBlock() || document.getElementById('root');
  target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function searchProduct(productName: string): void {
  openFullCatalog(false);
  const input = findSearchInput();
  if (!input) return;
  input.focus();
  setNativeInputValue(input, productName);
  scrollToCatalogList();
}

function findCategoryCombobox(): HTMLElement | null {
  const filterRow = findFilterRow();
  const scope = filterRow || document;
  const comboboxes = Array.from(scope.querySelectorAll<HTMLElement>('button[role="combobox"], [role="combobox"], [aria-haspopup="listbox"]')).filter(
    (element) => !element.closest(`#${SECTION_ID}`)
  );

  if (comboboxes.length) return comboboxes[0];
  return null;
}

function clickElement(element: HTMLElement): void {
  element.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true, cancelable: true, pointerType: 'mouse' }));
  element.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
  element.dispatchEvent(new PointerEvent('pointerup', { bubbles: true, cancelable: true, pointerType: 'mouse' }));
  element.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
  element.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
}

function selectCategoryOption(category: string): boolean {
  const wanted = normalizeText(category);
  const options = Array.from(document.querySelectorAll<HTMLElement>('[role="option"], [data-radix-collection-item]'));
  const match = options.find((option) => normalizeText(option.textContent || '') === wanted) ||
    options.find((option) => normalizeText(option.textContent || '').includes(wanted));

  if (!match) return false;
  clickElement(match);
  return true;
}

function filterByCategory(category: string): void {
  openFullCatalog(false);
  const combobox = findCategoryCombobox();
  if (!combobox) {
    scrollToCatalogList();
    return;
  }

  clickElement(combobox);
  window.setTimeout(() => {
    const ok = selectCategoryOption(category);
    if (!ok) {
      const input = findSearchInput();
      if (input) setNativeInputValue(input, category);
    }
    scrollToCatalogList();
  }, 180);
}

function resetCategory(): void {
  const combobox = findCategoryCombobox();
  if (combobox) {
    clickElement(combobox);
    window.setTimeout(() => {
      const options = Array.from(document.querySelectorAll<HTMLElement>('[role="option"], [data-radix-collection-item]'));
      const all = options.find((option) => normalizeText(option.textContent || '').includes('tutte le categorie'));
      if (all) clickElement(all);
    }, 180);
  }

  const input = findSearchInput();
  if (input) setNativeInputValue(input, '');
  scrollToCatalogList();
}

function getCatalogVisibilityTargets(): HTMLElement[] {
  const targets = new Set<HTMLElement>();
  const filterRow = findFilterRow();
  const results = findCatalogResultsContainer();
  if (filterRow) targets.add(filterRow);
  if (results) targets.add(results);
  return [...targets].filter((element) => !element.closest(`#${SECTION_ID}`));
}

function updateFullCatalogVisibility(): void {
  syncFullCatalogStateFromUrl();

  if (!isCatalogRoute()) {
    document.body.classList.remove('im-catalog-preview-mode');
    return;
  }

  const targets = getCatalogVisibilityTargets();
  const previewMode = !state.fullCatalogOpen;

  document.body.classList.toggle('im-catalog-preview-mode', previewMode);
  targets.forEach((element) => {
    element.classList.toggle('im-catalog-full-hidden', previewMode);
  });
}

function openFullCatalog(scroll = true): void {
  state.fullCatalogOpen = true;
  updateUrlForFullCatalog(true);
  updateFullCatalogVisibility();
  updateSection();
  if (scroll) window.setTimeout(scrollToCatalogList, 80);
}

function closeFullCatalog(): void {
  state.fullCatalogOpen = false;
  updateUrlForFullCatalog(false);
  updateFullCatalogVisibility();
  updateSection();
  document.getElementById(SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function handleSectionClick(event: MouseEvent): void {
  const target = event.target as HTMLElement | null;
  if (!target) return;

  const retry = target.closest<HTMLElement>('[data-im-retry-macros]');
  if (retry) {
    void loadProducts(true);
    return;
  }

  const openFull = target.closest<HTMLElement>('[data-im-open-full-catalog]');
  if (openFull) {
    openFullCatalog();
    return;
  }

  const closeFull = target.closest<HTMLElement>('[data-im-close-full-catalog]');
  if (closeFull) {
    closeFullCatalog();
    return;
  }

  const reset = target.closest<HTMLElement>('[data-im-reset-category]');
  if (reset) {
    resetCategory();
    return;
  }

  const product = target.closest<HTMLElement>('[data-im-product-search]');
  if (product?.dataset.imProductSearch) {
    searchProduct(product.dataset.imProductSearch);
    return;
  }

  const openCategory = target.closest<HTMLElement>('[data-im-open-category]');
  if (openCategory?.dataset.imOpenCategory) {
    filterByCategory(openCategory.dataset.imOpenCategory);
    return;
  }

  const toggle = target.closest<HTMLElement>('[data-im-toggle-macro]');
  if (toggle?.dataset.imToggleMacro) {
    state.activeMacroId = state.activeMacroId === toggle.dataset.imToggleMacro ? '' : toggle.dataset.imToggleMacro;
    updateSection();
  }
}

async function loadProducts(force = false): Promise<void> {
  if (state.loading) return;
  if (state.loaded && !force) return;

  state.loading = true;
  state.error = '';
  updateSection();

  try {
    const products = await fetchCatalogProducts();
    state.products = products;
    state.macros = buildMacroCategories(products);
    state.loaded = true;
  } catch (error) {
    console.error('Errore macrocard catalogo:', error);
    state.error = error instanceof Error ? error.message : 'Errore sconosciuto.';
  } finally {
    state.loading = false;
    updateSection();
  }
}

function scheduleUpdate(): void {
  window.requestAnimationFrame(() => {
    syncFullCatalogStateFromUrl();
    updateSection();
    updateFullCatalogVisibility();
    if (isCatalogRoute()) void loadProducts();
  });
}

function patchHistoryEvents(): void {
  const notify = () => window.dispatchEvent(new Event('im:navigation-change'));
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function pushState(...args) {
    const result = originalPushState.apply(this, args);
    notify();
    return result;
  };

  history.replaceState = function replaceState(...args) {
    const result = originalReplaceState.apply(this, args);
    notify();
    return result;
  };

  window.addEventListener('popstate', notify);
}

export function initCatalogMacroCards(): void {
  onReady(() => {
    patchHistoryEvents();
    scheduleUpdate();

    let scheduled = false;
    const observer = new MutationObserver((mutations) => {
      const onlyMacroMutations = mutations.every((mutation) => {
        const target = mutation.target instanceof HTMLElement ? mutation.target : mutation.target.parentNode instanceof HTMLElement ? mutation.target.parentNode : null;
        return Boolean(target?.closest(`#${SECTION_ID}`));
      });
      if (onlyMacroMutations) return;
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        scheduleUpdate();
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('im:legacy-loaded', scheduleUpdate);
    window.addEventListener('im:navigation-change', scheduleUpdate);
  });
}
